import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { AIChatResponseEventFragment } from 'types/graphql'

import AIChatInputArea from 'src/components/AIChatInputArea'
import ContentAreaOverlay from 'src/components/ContentAreaOverlay/ContentAreaOverlay'
import { getPageContext, getPageSource } from 'src/components/PageContext'
import { buildHtmlPageContext } from 'src/components/PageContextHtml/build-html-page-context'
import {
  StaffAIChatMessage,
  StaffAIViewMode,
  useStaffAIAssistant,
} from 'src/components/StaffAIAssistant/StaffAIAssistantContext'
import { useSubscribeToAIChatSessionResponses } from 'src/fetch/aiChat'
import {
  useAddOptimisticSession,
  useCreateStaffAIChatMutation,
  useGetStaffAIChatSessionQuery,
  useGetStaffAIChatSuggestedPromptsQuery,
  useInvalidateStaffAIChatSessions,
} from 'src/fetch/staffAIChat'
import { Icon, T, VStack } from 'src/govwell-ui'
import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { useIsInternalStaffAIEnabled } from 'src/utils/featureFlagging/featureFlagging'

import StaffAIChatEmptyState from './StaffAIChatEmptyState'
import StaffAIChatHeader from './StaffAIChatHeader'
import StaffAIChatMessageItem from './StaffAIChatMessageItem'
import StaffAIChatSidebar from './StaffAIChatSidebar'

/**
 * Updates the last message in the messages array.
 */
const updateLastMessage = (
  update: (message: StaffAIChatMessage) => StaffAIChatMessage
): React.SetStateAction<StaffAIChatMessage[]> => {
  return (prev) => {
    const lastMessage = prev[prev.length - 1]
    if (!lastMessage) {
      return prev
    }
    return [...prev.slice(0, -1), update(lastMessage)]
  }
}

type StaffAIAssistantOverlayContentProps = {
  onClose: () => void
  viewMode: StaffAIViewMode
  onMaximize: () => void
  onMinimize: () => void
}

export const StaffAIAssistantOverlayContent = ({
  onClose,
  viewMode,
  onMaximize,
  onMinimize,
}: StaffAIAssistantOverlayContentProps) => {
  const context = useStaffAIAssistant()
  const userTextInput = context?.userTextInput ?? ''
  const setUserTextInput = context?.setUserTextInput ?? (() => {})
  const searchQuery = context?.searchQuery ?? ''
  const setSearchQuery = context?.setSearchQuery ?? (() => {})
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const prevActiveSessionIdRef = useRef<number | null>(null)
  // Track whether we just loaded a session (should scroll instantly, not animate)
  const justLoadedSessionRef = useRef(false)

  // Get state from context (messages persist across overlay open/close)
  const messages = context?.messages ?? []
  const setMessages = context?.setMessages ?? (() => {})
  const isAssistantTyping = context?.isAssistantTyping ?? false
  const setIsAssistantTyping = context?.setIsAssistantTyping ?? (() => {})

  // Determine if we should show the sidebar (only in full view mode)
  const showSidebar = viewMode === 'full'

  // Get chat storage IDs from context (for new conversations)
  const chatLocalStorageId = context?.chatStorageIds.chatLocalStorageId ?? ''
  const chatSessionStorageId = context?.chatStorageIds.chatSessionStorageId ?? ''

  const { mutateAsync: createStaffAIChat } = useCreateStaffAIChatMutation()
  const organizationId = useMyOrgId()
  const { suggestedPrompts, isLoading: isSuggestedPromptsLoading } =
    useGetStaffAIChatSuggestedPromptsQuery()
  const invalidateStaffAIChatSessions = useInvalidateStaffAIChatSessions()
  const addOptimisticSession = useAddOptimisticSession()

  // Load session data when selecting a past conversation
  const { session: loadedSession, isLoading: isLoadingSession } = useGetStaffAIChatSessionQuery(
    context?.activeSessionId ?? null
  )

  // Load messages from a selected session
  useEffect(() => {
    if (!context) {
      return
    }

    // Detect when active session changes
    const activeSessionId = context.activeSessionId
    const prevActiveSessionId = prevActiveSessionIdRef.current

    // When switching to a new conversation (null session ID), clear messages
    if (activeSessionId === null && prevActiveSessionId !== null) {
      setMessages([])
      context.setIsLoadingSession(false)
    }

    // When session data is loaded, populate the messages
    if (loadedSession && activeSessionId === loadedSession.id && context.isLoadingSession) {
      const loadedMessages: StaffAIChatMessage[] = []
      for (const chat of loadedSession.chats ?? []) {
        // Add user message
        loadedMessages.push({
          id: `user-${chat.id}`,
          text: chat.question ?? '',
          sender: 'user',
          questionCreatedAt: chat.questionCreatedAt,
        })
        // Add AI response
        loadedMessages.push({
          id: chat.id,
          text: chat.response ?? '',
          sender: 'ai',
          questionCreatedAt: chat.questionCreatedAt,
          responseCreatedAt: chat.responseCreatedAt,
          citations: chat.citations ?? undefined,
          isThinking: false,
        })
      }
      setMessages(loadedMessages)
      justLoadedSessionRef.current = true
      context.setIsLoadingSession(false)
    }

    prevActiveSessionIdRef.current = activeSessionId
  }, [context, loadedSession, setMessages])

  // Subscribe to streaming responses
  const onResponseEvent = useCallback(
    (event: AIChatResponseEventFragment) => {
      if (event.__typename === 'AIChatTextUpdate') {
        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            text: event.fullText,
            isThinking: !event.done,
          }))
        )
      } else if (event.__typename === 'AIChatTextDelta') {
        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            text: lastMessage.text + (event.text || ''),
            isThinking: !event.done,
          }))
        )
      }
    },
    [setMessages]
  )

  useSubscribeToAIChatSessionResponses({
    chatSessionStorageId,
    onResponseEvent,
  })

  // Auto-scroll to bottom when messages update
  // Use instant scroll when loading a past conversation, smooth for new messages
  useEffect(() => {
    const behavior = justLoadedSessionRef.current ? 'instant' : 'smooth'
    messagesEndRef.current?.scrollIntoView({ behavior })
    justLoadedSessionRef.current = false
  }, [messages])

  const handleSendMessage = useCallback(
    async (autoSendText?: string) => {
      const textMessage = autoSendText ?? userTextInput
      if (!textMessage.trim() || isAssistantTyping) {
        return
      }

      // Check if this is the first message in a new conversation
      const isFirstMessageInNewChat = messages.length === 0 && !context?.activeSessionId

      // Add user message and placeholder for AI response
      setMessages((prev) => [
        ...prev,
        {
          id: `user-${Date.now()}`,
          text: textMessage,
          sender: 'user',
          questionCreatedAt: new Date().toISOString(),
        },
        {
          id: `ai-${Date.now()}`,
          text: '',
          sender: 'ai',
          isThinking: true,
        },
      ])
      setUserTextInput('')
      setIsAssistantTyping(true)

      // If this is the first message in a new chat, immediately add an optimistic
      // session to the sidebar so the user sees the conversation appear right away
      if (isFirstMessageInNewChat) {
        addOptimisticSession(textMessage)
      }

      try {
        const pageContext = getPageContext()
        const pageContextText = buildHtmlPageContext({
          viewer: 'staff',
          structuredPageContextText: pageContext?.serialized ?? undefined,
          structuredPageContextEnvelope: pageContext?.envelope,
        })
        const result = await createStaffAIChat({
          input: {
            chatLocalStorageId,
            chatSessionStorageId,
            question: textMessage,
            sessionType: 'Internal',
            organizationId,
            pageContextText,
            pageSource: getPageSource('staff'),
          },
        })

        // Update the last message with final response data
        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            id: result.createAIChat.id,
            questionCreatedAt: result.createAIChat.questionCreatedAt ?? '',
            responseCreatedAt: result.createAIChat.responseCreatedAt ?? '',
            isThinking: false,
            ...(result.createAIChat.response ? { text: result.createAIChat.response } : {}),
            ...(result.createAIChat.citations?.length
              ? { citations: result.createAIChat.citations }
              : {}),
          }))
        )

        // Invalidate sessions list to replace optimistic session with real data
        invalidateStaffAIChatSessions()
      } catch {
        setMessages(
          updateLastMessage((lastMessage) => ({
            ...lastMessage,
            text: '<div>Sorry, something went wrong. Please try again.</div>',
            isThinking: false,
          }))
        )
      } finally {
        setIsAssistantTyping(false)
      }
    },
    [
      userTextInput,
      isAssistantTyping,
      messages.length,
      context?.activeSessionId,
      setMessages,
      setIsAssistantTyping,
      addOptimisticSession,
      createStaffAIChat,
      chatLocalStorageId,
      chatSessionStorageId,
      invalidateStaffAIChatSessions,
      organizationId,
    ]
  )

  const handleSelectPrompt = useCallback(
    (prompt: string) => {
      void handleSendMessage(prompt)
    },
    [handleSendMessage]
  )

  // Use prompts from backend query (no duplicate defaults needed)
  const displayPrompts = useMemo(
    () => suggestedPrompts.slice(0, 3).map((s) => s.prompt),
    [suggestedPrompts]
  )

  const hasMessages = messages.length > 0
  const showLoadingState = context?.isLoadingSession || isLoadingSession

  // Compact empty state for side view
  const SideViewEmptyState = useMemo(
    () => (
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-8">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-neutral-900">
          <Icon.FaSparkles className="h-7 w-7 text-white" />
        </div>
        <T.Body className="mb-1 text-center font-semibold text-neutral-900">
          GovWell AI Assistant
        </T.Body>
        <T.BodySmall className="mb-4 text-center text-neutral-600">
          Ask about your records, workflows, and configurations.
        </T.BodySmall>
      </div>
    ),
    []
  )

  return (
    <div className="flex h-full bg-neutral-50">
      {/* Left Sidebar - only shown in full view mode */}
      {showSidebar && (
        <StaffAIChatSidebar searchQuery={searchQuery} onSearchQueryChange={setSearchQuery} />
      )}

      {/* Main Chat Area */}
      <div className="flex min-w-0 flex-1 flex-col">
        <StaffAIChatHeader
          onClose={onClose}
          viewMode={viewMode}
          onMaximize={onMaximize}
          onMinimize={onMinimize}
          showSidebar={showSidebar}
        />

        {/* Main Content Area */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {showLoadingState ? (
            <div className="flex flex-1 items-center justify-center">
              <div className="text-neutral-500">Loading conversation...</div>
            </div>
          ) : !hasMessages ? (
            showSidebar ? (
              <StaffAIChatEmptyState />
            ) : (
              SideViewEmptyState
            )
          ) : (
            <div
              className={`mx-auto w-full flex-1 py-4 ${showSidebar ? 'max-w-4xl px-6' : 'px-4'}`}
            >
              <VStack gap={4} align="stretch">
                {messages.map((msg) => (
                  <StaffAIChatMessageItem key={msg.id} message={msg} />
                ))}
                <div ref={messagesEndRef} />
              </VStack>
            </div>
          )}
        </div>

        <AIChatInputArea
          value={userTextInput}
          onValueChange={setUserTextInput}
          onSend={() => handleSendMessage()}
          isDisabled={isAssistantTyping || showLoadingState}
          suggestedPrompts={displayPrompts}
          onSelectPrompt={handleSelectPrompt}
          showPrompts={!hasMessages && !isSuggestedPromptsLoading && !showLoadingState}
          disclaimer="GovWell AI can make mistakes. Check important info."
        />
      </div>
    </div>
  )
}

/**
 * Overlay component that handles the full modal view of the AI Assistant.
 * The side panel view is rendered inline in SidebarLayout instead.
 */
const StaffAIAssistantOverlay = () => {
  const isInternalStaffAIEnabled = useIsInternalStaffAIEnabled()
  const context = useStaffAIAssistant()

  // Guard: feature flag must be enabled and context must be available
  if (!isInternalStaffAIEnabled || !context) {
    return null
  }

  const { viewMode, overlay, deferredClose, maximize, minimize } = context

  // Side panel view is rendered inline in SidebarLayout, not here
  if (viewMode === 'side') {
    return null
  }

  // Render full modal view
  // Uses deferredClose for click-outside so that openFullView (from the sidebar button)
  // can cancel the close before it executes, preventing flicker.
  return (
    <ContentAreaOverlay isOpen={overlay.isOpen} onClose={deferredClose}>
      <StaffAIAssistantOverlayContent
        onClose={overlay.close}
        viewMode={viewMode}
        onMaximize={maximize}
        onMinimize={minimize}
      />
    </ContentAreaOverlay>
  )
}

export default React.memo(StaffAIAssistantOverlay)
